import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, BookOpen, ChevronDown, ChevronRight, KeyRound, Menu, Search, X } from 'lucide-react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { searchEntries } from '../lib.js';
import { platformIcon } from '../platform-icons.js';

export function Brand({ compact = false }) {
  return <Link className="brand" to="/"><img src="/logo.png" alt="" /><span>{compact ? 'VideoCaptioner' : 'VideoCaptioner API'}</span></Link>;
}

export function Header({ onSearch, onNavigation, docsMode = false }) {
  return <header className="site-header">
    <div className="header-brand-slot"><button className="header-menu-button" onClick={onNavigation} aria-label={docsMode ? '打开文档目录' : '打开网站导航'}><Menu size={19} /></button><Brand /></div>
    <nav className="header-nav" aria-label="主导航">
      <NavLink to="/guides/quickstart">开发指南</NavLink>
      <NavLink to="/reference/v1">API 参考</NavLink>
      <NavLink to="/models">模型广场</NavLink>
    </nav>
    <div className="header-actions">
      <button className="header-search" onClick={onSearch}><Search size={16} /><span>搜索文档</span><kbd>⌘K</kbd></button>
      <a className="header-key" href="https://api.videocaptioner.cn/console/token"><KeyRound size={15} /><span>获取 API Key</span></a>
    </div>
  </header>;
}

function PrimaryNavigation({ onNavigate }) {
  return <nav className="mobile-primary-nav" aria-label="网站导航">
    <NavLink to="/" end onClick={onNavigate}>文档首页</NavLink>
    <NavLink to="/guides/quickstart" onClick={onNavigate}>开发指南</NavLink>
    <NavLink to="/reference/v1" onClick={onNavigate}>API 参考</NavLink>
    <NavLink to="/models" onClick={onNavigate}>模型广场</NavLink>
  </nav>;
}

export function MobileNavigation({ open, onClose }) {
  return <>
    <aside className={open ? 'mobile-site-menu is-open' : 'mobile-site-menu'}>
      <div className="sidebar-mobile-head"><Brand compact /><button onClick={onClose} aria-label="关闭导航"><X size={19} /></button></div>
      <PrimaryNavigation onNavigate={onClose} />
      <a className="mobile-token-link" href="https://api.videocaptioner.cn/console/token"><KeyRound size={16} />获取 API Key</a>
    </aside>
    {open && <button className="sidebar-scrim" onClick={onClose} aria-label="关闭导航" />}
  </>;
}

export function SearchDialog({ open, onClose, searchIndex, catalog }) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const results = useMemo(() => searchEntries(searchIndex || [], query), [searchIndex, query]);

  useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    setActive(0);
    document.body.classList.add('dialog-open');
    window.setTimeout(() => inputRef.current?.focus(), 30);
    const close = (event) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', close);
    return () => { document.body.classList.remove('dialog-open'); document.removeEventListener('keydown', close); };
  }, [open, onClose]);

  useEffect(() => setActive(0), [query]);
  if (!open) return null;

  const choose = (entry) => {
    navigate(entry.type === 'guide' ? `/guides/${entry.id}` : `/reference/${entry.platform}/${entry.id}`);
    onClose();
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') { event.preventDefault(); setActive((value) => Math.min(results.length - 1, value + 1)); }
    if (event.key === 'ArrowUp') { event.preventDefault(); setActive((value) => Math.max(0, value - 1)); }
    if (event.key === 'Enter' && results[active]) { event.preventDefault(); choose(results[active]); }
  };

  return <div className="search-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <section className="search-dialog" role="dialog" aria-modal="true" aria-label="搜索文档">
      <div className="search-input-row"><Search size={20} /><input ref={inputRef} value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={onKeyDown} placeholder="搜索接口、路径或指南…" /><button onClick={onClose} aria-label="关闭搜索"><X size={18} /></button></div>
      <div className="search-results">
        {!query && <div className="search-empty"><b>从哪里开始？</b><p>试试搜索“图片生成”“Claude”或“/v1/models”。</p></div>}
        {query && results.length === 0 && <div className="search-empty"><b>没有匹配结果</b><p>换一个更短的关键词试试。</p></div>}
        {results.map((entry, index) => {
          const platform = catalog.platforms.find((item) => item.id === entry.platform);
          return <button key={`${entry.type}-${entry.platform || ''}-${entry.id}`} className={active === index ? 'active' : ''} onMouseEnter={() => setActive(index)} onClick={() => choose(entry)}>
            <span className="search-result-icon">{entry.type === 'guide' ? <BookOpen size={17} /> : <img src={platformIcon(platform)} alt="" />}</span>
            <span><b>{entry.title}</b><small>{entry.subtitle}</small></span><ArrowRight size={15} />
          </button>;
        })}
      </div>
      <footer><span><kbd>↑↓</kbd> 选择</span><span><kbd>↵</kbd> 打开</span><span><kbd>Esc</kbd> 关闭</span></footer>
    </section>
  </div>;
}

function PlatformOperations({ platform, operations, activeOperation, onNavigate }) {
  const tags = [...new Set((operations || []).map((item) => item.tag))];
  if (!operations?.length) return null;
  return <div className="sidebar-operation-tree">{tags.map((tag) => <section key={tag}>
    <span>{tag}</span>
    {operations.filter((item) => item.tag === tag).map((operation) => <Link key={operation.id} className={operation.id === activeOperation ? 'active' : ''} to={`/reference/${platform}/${operation.id}`} onClick={onNavigate}>
      <small className={`method-dot ${operation.method.toLowerCase()}`}>{operation.method}</small><em>{operation.summary}</em>
    </Link>)}
  </section>)}</div>;
}

export function DocsSidebar({ open, onClose, mode, catalog, platform: platformId, operations, activeOperation, onSearch }) {
  const [collapsedPlatform, setCollapsedPlatform] = useState('');
  const loadingReference = mode === 'reference' && operations === null;
  useEffect(() => setCollapsedPlatform(''), [platformId]);
  return <>
    <aside className={open ? 'docs-sidebar is-open' : 'docs-sidebar'}>
      <div className="sidebar-mobile-head"><Brand compact /><button onClick={onClose} aria-label="关闭目录"><X size={19} /></button></div>
      <PrimaryNavigation onNavigate={onClose} />
      <button className="sidebar-search" onClick={onSearch}><Search size={16} /><span>搜索文档</span><kbd>⌘K</kbd></button>
      <div className="sidebar-scroll" aria-busy={loadingReference}>
        {mode === 'guides' ? [...new Set(catalog.guides.map((item) => item.section))].map((section) => <section className="sidebar-section" key={section}>
          <h3>{section}</h3>
          {catalog.guides.filter((item) => item.section === section).map((guide) => <NavLink key={guide.slug} to={`/guides/${guide.slug}`} onClick={onClose}>{guide.brandIcon ? <img className="guide-brand-icon" src={platformIcon({ icon: guide.brandIcon })} alt="" /> : <BookOpen size={15} />}<span>{guide.title}</span></NavLink>)}
        </section>) : loadingReference ? null : catalog.platformGroups.map((group) => {
          const platforms = group.platforms.map((id) => catalog.platforms.find((item) => item.id === id)).filter(Boolean);
          if (!platforms.length) return null;
          return <section className="sidebar-section platform-group" key={group.id}>
            <h3>{group.title}</h3>
            {platforms.map((platform) => {
              const active = platform.id === platformId;
              const expanded = active && collapsedPlatform !== platform.id;
              const contents = <><img src={platformIcon(platform)} alt="" /><span>{platform.name}</span><small>{platform.operationCount}</small>{expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</>;
              return <div className={active ? 'platform-branch active' : 'platform-branch'} key={platform.id}>
                {active
                  ? <button type="button" className="platform-link" aria-expanded={expanded} onClick={() => setCollapsedPlatform(expanded ? platform.id : '')}>{contents}</button>
                  : <Link className="platform-link" to={`/reference/${platform.id}`} onClick={onClose}>{contents}</Link>}
                {expanded && <PlatformOperations platform={platform.id} operations={operations} activeOperation={activeOperation} onNavigate={onClose} />}
              </div>;
            })}
          </section>;
        })}
      </div>
    </aside>
    {open && <button className="sidebar-scrim" onClick={onClose} aria-label="关闭目录" />}
  </>;
}
